import { HttpHandler, HttpInterceptor, HttpRequest, HttpEvent, HttpClient } from '@angular/common/http';
import { Observable, PartialObserver } from 'rxjs';
import { tap } from 'rxjs/operators';

export abstract class AbstractHttpService extends HttpClient {

  constructor(
    protected httpHandler: HttpHandler,
  ) {
    super(httpHandler);
  }

  /**
   * @example ```ts
   *  instanceFactory(handler: HttpHandler) {
   *     return new CustomHttpService(handler, this.dependencia) as this;
   *  }
   *  ```
   */
  abstract instanceFactory(handler: HttpHandler): this;

  useBaseUrl(baseUrl: string, ref = 'baseUrl') {
    if (baseUrl) {
      return this.use((req, next) => next.handle(
        req.clone({
          url: `${trimSlash(baseUrl)}/${trimSlash(req.url)}`,
        })
      ), ref);
    } else {
      return this;
    }
  }

  use(middleware: HttpInterceptor['intercept'], ref?: string) {
    return this.useInterceptor({
      intercept: middleware,
    }, ref);
  }

  useSideEffect(observer: PartialObserver<HttpEvent<any>>, beforeRequest?: () => void, ref?: string) {
    return this.use((req, next) => {
      if (beforeRequest) { beforeRequest(); }

      return next.handle(req).pipe(
        tap(observer)
      );
    }, ref);
  }

  useInterceptor(interceptor: HttpInterceptor, ref?: string): this {
    const handler: HttpHandler = new HttpInterceptorHandler(this.httpHandler, interceptor, ref);
    const result = this.instanceFactory(handler) as this;
    return result;
  }

  disableInterceptor(ref: string) {
    if (this.httpHandler instanceof HttpInterceptorHandler) {
      const newHandler = this.httpHandler.clone();
      this.httpHandler = newHandler;
      // tslint:disable-next-line: no-string-literal
      this['handler'] = newHandler;
    }

    let handler = this.httpHandler;
    while (handler instanceof HttpHandler) {
      if (handler instanceof HttpInterceptorHandler) {
        if (handler.ref == ref) {
          handler.disable();
        }

        handler = handler.next;
      } else {
        handler = null;
      }
    }

    return this;
  }

}

class HttpInterceptorHandler extends HttpHandler {
  enabled = true;

  constructor(
    public next: HttpHandler,
    public interceptor: HttpInterceptor,
    public ref?: string,
  ) {
    super();
  }

  handle(req: HttpRequest<any>): Observable<HttpEvent<any>> {
    if (this.enabled) {
      return this.interceptor.intercept(req, this.next);
    } else {
      return this.next.handle(req);
    }
  }

  clone(): HttpInterceptorHandler {
    return new HttpInterceptorHandler(
      this.next instanceof HttpInterceptorHandler ? this.next.clone() : this.next,
      this.interceptor,
      this.ref
    );
  }

  disable() {
    this.enabled = false;
  }

  enable() {
    this.enabled = true;
  }
}

/**
 * @example '/teste/teste/' => teste/teste
 */
function trimSlash(text: string = '') {
  return text.replace(/^\//, '').replace(/\/$/, '');
}
